import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Container from "../components/layout/Container";
import SubNav, { defensieSubNavItems } from "../components/SubNav";
import OfficialStandardsSection from "../components/preparation/OfficialStandardsSection";
import { getOfficialStandardText } from "../data/defensieStandards";

const pageUrl = "https://missionmovement.nl/cluster-6-voorbereiding-vrouwen";
const metaDescription =
  "Gerichte voorbereiding voor vrouwen die willen opbouwen richting Defensie Cluster 6, Korps Mariniers, KCT, Luchtmobiel, NLMARSOF of andere fysiek zware militaire richtingen. Geen officieel Defensie-traject, wel professionele begeleiding in belastbaarheid, kracht, hardlopen, bepakking en herstel.";

const startTrajectoryItems = [
  "12-weeks Cluster 6 trainingsprogramma voor vrouwen",
  "Cluster 6 Guide voor Vrouwen",
  "pull-up en push-up progressies",
  "opbouwadvies voor hardlopen en bepakking",
  "uitleg over voeding, herstel en trainen rond je cyclus",
  "startcheck/intake",
  "persoonlijk startadvies van Justin",
  "2 weken WhatsApp-startsupport",
  "progressietracker",
];

const startTrajectoryCardItems = [
  "programma en guide",
  "intake en persoonlijk startadvies",
  "progressies voor kracht, lopen en bepakking",
  "2 weken WhatsApp-startsupport",
];

const fitItems = [
  {
    title: "Richting zware Defensiefuncties",
    text: "Voor vrouwen die richting Korps Mariniers, KCT, Luchtmobiel, NLMARSOF of andere fysiek zware Defensierichtingen willen werken.",
  },
  {
    title: "Cluster 6 en aanvullende tests",
    text: "Voor vrouwen die zich voorbereiden op de functiegebonden aanstellingskeuring. Aanvullende eenheidstests en latere selectieonderdelen vragen een eigen voorbereiding.",
  },
  {
    title: "Meer dan net genoeg",
    text: "Voor kandidaten die niet alleen de eisen willen aantikken, maar extra marge willen opbouwen in kracht, hardlopen, bepakking, zwemmen en herstel.",
  },
  {
    title: "Duurzaam en klachtenvrij opbouwen",
    text: "Voor vrouwen die serieus willen trainen, maar de opbouw willen laten aansluiten op hun huidige niveau, belastbaarheid, herstel en blessuregeschiedenis.",
  },
];

const buildUpItems = [
  "pees-, gewrichts- en botbelasting hebben tijd nodig om zich aan te passen",
  "hardlopen, krachttraining en bepakking moeten elkaar versterken",
  "te snel opschalen vergroot de kans op klachten en trainingsverlies",
  "een goede route bouwt marge op voordat de belasting echt zwaar wordt",
];

const bottleneckItems = [
  "aerobe basis en 12-minuten loopvermogen",
  "bovenlichaamkracht en krachtuithoudingsvermogen",
  "trekken, duwen, dragen en tillen",
  "bepakking geleidelijk opbouwen",
  "voeten, kuiten, knieen, heupen en onderrug laten wennen aan draaglast",
  "voldoende eten om herstel en adaptatie mogelijk te maken",
  "krachttraining combineren met lopen, zwemmen en herstellen",
  "trainingsstructuur in plaats van willekeurig meer doen",
];

const internalLinks = [
  {
    label: "Voorbereiding Defensie",
    path: "/voorbereiding-defensie",
    text: "De brede basis achter fysieke en mentale voorbereiding richting Defensie.",
  },
  {
    label: "Korps Mariniers",
    path: "/units/mariniers",
    text: "Verdiep je in de route richting de mariniersopleiding.",
  },
  {
    label: "Korps Commando Troepen",
    path: "/units/commandotroepen",
    text: "Lees meer over de richting waar veel kandidaten KCT mee bedoelen.",
  },
  {
    label: "11 Luchtmobiele Brigade",
    path: "/units/luchtmobiel",
    text: "Bekijk de fysieke accenten van de luchtmobiele route.",
  },
  {
    label: "Gratis voorbereidings-check",
    path: "/voorbereiding-check",
    text: "Krijg een eerste beeld van je huidige voorbereiding.",
  },
  {
    label: "Contact en intake",
    path: "/contact#contact-form",
    text: "Stel je vraag of vraag persoonlijk advies aan.",
  },
];

const faqItems = [
  {
    question: "Is Cluster 6 hetzelfde als de fysieke tests van Mariniers of KCT?",
    answer: getOfficialStandardText("cluster6"),
  },
  {
    question: "Is dit hetzelfde als Athena Force van Defensie?",
    answer:
      "Nee. Mission Movement is geen onderdeel van Defensie en dit traject is geen officieel Athena Force traject. Het is een onafhankelijk voorbereidingsaanbod voor vrouwen die willen werken aan hun fysieke basis en belastbaarheid richting Cluster 6 en zware militaire richtingen.",
  },
  {
    question: "Kan dit helpen als ik richting KCT of Special Forces wil?",
    answer:
      "Het starttraject helpt je gericht bouwen aan kracht, hardlopen, bepakking, herstel en trainingsstructuur. Het garandeert geen selectie of toelating, maar helpt je de voorbereiding serieuzer en gestructureerder aan te pakken.",
  },
  {
    question: "Is dit ook relevant voor NLMARSOF of Korps Mariniers?",
    answer:
      "Een sterke fysieke basis kan relevant zijn voor zulke richtingen. Cluster 6 beschrijft de functiegebonden aanstellingskeuring; de Fitheidstest Mariniers, Kennismakingsdagen en latere interne selecties zijn afzonderlijke beoordelingen. Controleer je actuele vacature en uitnodiging; deze voorbereiding geeft geen garantie op toelating.",
  },
  {
    question: "Moet ik al op Cluster 6 niveau zitten?",
    answer:
      "Nee. Je kunt vanaf je huidige niveau opbouwen richting de functionele fysieke aanstellingskeuring die bij je vacature hoort. Cluster 6 is geen algemene lijst met Mariniers- of KCT-testonderdelen. De training moet passen bij je belastbaarheid, sportachtergrond, herstel en eventuele klachten.",
  },
  {
    question: "Waarom specifiek voor vrouwen?",
    answer:
      "De standaard wordt niet verlaagd voor vrouwen. De weg ernaartoe verschilt wel per persoon. Veel vrouwen hebben baat bij een zorgvuldige opbouw van kracht, loopbelasting, bepakking en herstel, zeker wanneer ze richting fysiek zware militaire functies willen werken.",
  },
  {
    question: "Wat is het verschil tussen het begeleide traject en het startpakket?",
    answer:
      "Het Cluster 6 Starttraject voor Vrouwen is nu beschikbaar en combineert een 12-weeks programma met intake, persoonlijk startadvies en korte startsupport. Het zelfstandige startpakket komt later beschikbaar. Intensieve 1-op-1 coaching is op aanvraag.",
  },
  {
    question: "Is dit alleen voor vrouwen die al bij Defensie zitten?",
    answer:
      "Nee. Het is ook bedoeld voor vrouwen die zich orienteren, willen solliciteren of eerst willen ontdekken waar ze fysiek staan.",
  },
];

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Cluster 6 Voorbereiding voor Vrouwen",
  url: pageUrl,
  inLanguage: "nl-NL",
  description: metaDescription,
  publisher: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
};

const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

const Cluster6VoorbereidingVrouwen = () => {
  return (
    <main className="min-h-screen bg-mmPage text-mmText">
      <Helmet>
        <title>
          Cluster 6 Voorbereiding voor Vrouwen | KCT, Mariniers en Special
          Forces voorbereiding
        </title>
        <meta name="description" content={metaDescription} />
        <meta
          property="og:title"
          content="Cluster 6 Voorbereiding voor Vrouwen | KCT, Mariniers en Special Forces voorbereiding"
        />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content="https://missionmovement.nl/img/Womens_Tactical_Shooting_Line.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
      </Helmet>

      <section className="relative min-h-[560px] md:min-h-[640px] flex items-center overflow-hidden border-b border-mmBorder">
        <img
          src="/img/Womens_Tactical_Shooting_Line.png"
          alt="Fysieke voorbereiding met bepakking"
          className="absolute inset-0 h-full w-full object-cover object-[58%_center] grayscale saturate-[0.65] brightness-[0.72] md:object-[52%_center]"
          width={1672}
          height={941}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="absolute inset-0 mm-heroOverlay" />
        <div className="absolute inset-0 bg-black/25 md:bg-black/10" />

        <Container className="relative z-10 py-16 md:py-20">
          <div className="max-w-4xl">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-5">
              Cluster 6 Voorbereiding voor Vrouwen
            </p>
            <h1 className="font-display uppercase tracking-[0.08em] sm:tracking-widest text-white text-[34px] sm:text-[42px] md:text-[68px] leading-[0.98] break-words max-w-full">
              Cluster 6 Voorbereiding voor Vrouwen
            </h1>
            <div className="h-[3px] w-20 bg-mmAccent rounded-full my-6" />
            <div className="w-full max-w-3xl bg-black/65 border border-white/10 rounded-2xl px-5 py-4 shadow-sm md:px-6 md:py-5">
              <p className="text-white/95 text-base md:text-xl leading-relaxed">
                Voor vrouwen die serieus willen opbouwen richting fysiek zware
                Defensierichtingen zoals Korps Mariniers, KCT of Luchtmobiel,
                of zich oriënteren op een latere specialisatie zoals NLMARSOF.
                Het functiecluster volgt uit je vacature; aanvullende
                eenheidstests en selecties zijn afzonderlijke stappen.
              </p>
              <p className="mt-4 text-white/85 text-sm md:text-base leading-relaxed">
                Bereid je voor op het juiste testmoment. De weg ernaartoe moet
                passen bij jouw startniveau en belastbaarheid.
              </p>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contact#contact-form"
                className="mm-btnPrimary w-full sm:w-auto sm:max-w-none"
              >
                Vraag een intake aan
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <div className="relative shadow-sm">
        <SubNav variant="dark" scrollable items={defensieSubNavItems} />
      </div>

      <OfficialStandardsSection />

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Voor wie
            </p>
            <h2 className="mm-h2 mb-6">Voor wie is dit bedoeld?</h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Cluster 6-level voorbereiding vraagt meer dan alleen hard
                trainen. Het gaat om gericht sterker worden, beter leren lopen,
                bepakking rustig opbouwen en herstel serieus nemen. Zeker bij
                zware Defensierichtingen is de route naar inzetbaarheid vaak
                belangrijker dan een losse testscore of alleen het net halen
                van de eisen.
              </p>
              <p>
                Mission Movement is een onafhankelijk voorbereidingsplatform.
                Deze route is bedoeld om kandidaten beter voorbereid te laten
                starten, zonder garanties op toelating, selectie of plaatsing.
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {fitItems.map((item, index) => (
              <article key={item.title} className="mm-card p-6">
                <div className="h-9 w-9 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Ondergrens
            </p>
            <h2 className="mm-h2 mb-6">Cluster 6 is geen eindstation</h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Voldoen aan de functionele fysieke eisen voor Cluster 6
                betekent niet dat je ook een aanvullende Fitheidstest,
                Kennismakingsdag of latere selectie hebt afgerond. Het zijn
                verschillende beoordelingen.
              </p>
              <p>
                Voor zware gevechtsfuncties wil je niet mikken op net genoeg.
                Je wilt marge opbouwen, zodat je lichaam belasting kan dragen,
                herstellen en opnieuw leveren.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
                Praktijk
              </p>
              <h2 className="mm-h2 mb-6">
                Fysieke schakels die extra aandacht vragen
              </h2>
              <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
                <p>
                  De eisen worden niet lager. Daarom moet de voorbereiding niet
                  alleen zwaarder worden, maar vooral slimmer en beter
                  opgebouwd. Bij fysiek zware Defensiefuncties draait het om
                  meer dan motivatie: je lichaam moet leren omgaan met herhaalde
                  belasting, relatieve draaglast, herstel en specifieke kracht
                  die in gewone training vaak onvoldoende wordt opgebouwd.
                </p>
                <p>
                  Bij zware militaire taken spelen onder andere aerobe fitheid,
                  bovenlichaam-uithoudingsvermogen, bepakking, tillen, dragen,
                  herstel en blessuregeschiedenis een grote rol. Zeker bij zware
                  absolute lasten kan dezelfde uitrusting relatief zwaarder
                  uitpakken voor kleinere of lichtere kandidaten. Daarom trainen
                  we niet alleen harder, maar bouwen we gericht marge op.
                </p>
                <p>
                  Binnen dit traject kijken we daarom niet alleen naar de
                  eindnorm, maar vooral naar welke fysieke schakels nu de meeste
                  aandacht vragen. Het doel is niet om zo snel mogelijk kapot te
                  trainen, maar om genoeg marge op te bouwen om zware militaire
                  belasting beter te kunnen dragen.
                </p>
              </div>
            </div>

            <div className="mm-card p-6 md:p-8">
              <h3 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl mb-5">
                Trainingsfocus
              </h3>
              <ul className="space-y-3">
                {bottleneckItems.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-sm md:text-base text-mmTextMuted leading-relaxed"
                  >
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-mmAccent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
                Lange termijn
              </p>
              <h2 className="mm-h2 mb-6">
                Waarom geleidelijke opbouw telt
              </h2>
              <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
                <p>
                  Wie richting fysiek zware rollen werkt, heeft marge nodig.
                  Niet alleen op kracht of conditie, maar vooral in het vermogen
                  om week na week belasting te verwerken.
                </p>
                <p>
                  Daarom draait deze route om een rustige, meetbare opbouw. De
                  training wordt gekoppeld aan jouw startniveau, herstel en
                  belastbaarheid, zodat progressie niet afhankelijk wordt van
                  forceren.
                </p>
              </div>
            </div>

            <div className="mm-card p-6 md:p-8">
              <h3 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl mb-5">
                Waar we op sturen
              </h3>
              <ul className="space-y-3">
                {buildUpItems.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-sm md:text-base text-mmTextMuted leading-relaxed"
                  >
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-mmAccent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl mb-8">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Inhoud
            </p>
            <h2 className="mm-h2 mb-5">Wat zit er in het starttraject?</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Het starttraject bundelt de eerste 12 weken training, praktische
              uitleg en persoonlijk startadvies in een compacte route waarmee
              je gericht kunt beginnen.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {startTrajectoryItems.map((item) => (
              <div
                key={item}
                className="rounded-xl border border-mmBorder bg-mmPage/70 px-4 py-3"
              >
                <div className="flex gap-3 text-sm md:text-base text-mmTextMuted leading-relaxed">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-mmAccent" />
                  <span>{item}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmPage border-b border-mmBorder">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
                Trainingsfase
              </p>
              <h2 className="mm-h2 mb-6">Waarom 12 weken?</h2>
              <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
                <p>
                  12 weken is geen eindpunt. Het is een eerste duidelijke
                  trainingsfase waarin je leert hoe serieuze voorbereiding
                  werkt. Niet random meer doen en hopen dat je vanzelf sterker
                  wordt, maar trainen met opbouw, meetmomenten en een logische
                  planning richting waar je op welk moment moet staan.
                </p>
                <p>
                  Een brede voorbereiding vraagt herhaalde blootstelling en
                  tijd. Hardlopen, dragen en bepakking sluiten aan op de
                  functionele keuring. Pull-ups en push-ups trainen we ook als
                  krachtbasis en voor aanvullende eenheidstests; het zijn geen
                  onderdelen van de hierboven beschreven Cluster 6-keuring.
                  Deze vaardigheden en herstel worden niet structureel beter
                  door ze af en toe maximaal te testen. Je bouwt ze op door
                  progressieve belasting, voldoende herstel en technisch goede
                  herhaling. Je spieren, pezen, gewrichten en coordinatie
                  moeten de tijd krijgen om sterker en efficienter te worden.
                </p>
                <p>
                  Na 12 weken ben je dus niet klaar met je volledige
                  voorbereiding, maar je staat wel anders in het proces. Je weet
                  beter waar je staat, welke fysieke schakels aandacht vragen en
                  hoe je de volgende fase moet opbouwen.
                </p>
              </div>
            </div>

            <div className="mm-card p-6 md:p-8">
              <h3 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl mb-5">
                Na de eerste fase
              </h3>
              <ul className="space-y-3">
                {[
                  "zelfstandig verder met betere structuur",
                  "een nieuwe programmeringsfase laten maken",
                  "doorgaan met meer persoonlijke begeleiding als doel en tijdlijn daarom vragen",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-sm md:text-base text-mmTextMuted leading-relaxed"
                  >
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-mmAccent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      <section
        id="trajecten"
        className="py-16 md:py-20 bg-mmPage border-b border-mmBorder"
      >
        <Container>
          <div className="max-w-4xl mb-10">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Trajecten
            </p>
            <h2 className="mm-h2 mb-5">
              Kies je startpunt
            </h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Het Cluster 6 Starttraject voor Vrouwen is nu beschikbaar. Het
              zelfstandige pakket wordt later verder ingevuld.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
            <article className="mm-card flex flex-col p-6 md:p-7 opacity-80 lg:h-full">
              <div className="mb-3 inline-flex w-fit rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-mmTextMuted">
                Binnenkort beschikbaar
              </div>
              <h3 className="font-display uppercase tracking-widest text-mmText text-2xl md:text-3xl leading-tight mb-4">
                Zelfstandig Startpakket
              </h3>
              <p className="text-mmTextMuted text-sm md:text-base leading-relaxed mb-6">
                Voor zelfstandig starten met een gestructureerde opbouw richting
                Cluster 6. Dit pakket wordt later verder ingevuld.
              </p>
              <Link to="/contact#contact-form" className="mm-btn mt-auto">
                Vraag alvast informatie aan
              </Link>
            </article>

            <article className="mm-card relative flex flex-col overflow-hidden border-mmAccent/45 p-6 md:p-7 shadow-[0_18px_45px_-32px_rgba(255,204,1,0.55)]">
              <div className="absolute right-0 top-0 h-24 w-24 bg-mmAccent/10 blur-2xl" />
              <p className="font-display uppercase tracking-[0.2em] text-mmAccent text-sm md:text-base mb-2">
                Actief
              </p>
              <h3 className="font-display uppercase tracking-widest text-mmText text-2xl md:text-3xl leading-tight mb-3">
                Cluster 6 Starttraject voor Vrouwen
              </h3>
              <p className="font-display uppercase tracking-[0.16em] text-mmAccent text-3xl md:text-4xl leading-none mb-4">
                €139
              </p>
              <p className="text-mmTextMuted text-sm md:text-base leading-relaxed mb-5">
                12 weken gestructureerde voorbereiding met intake, persoonlijk
                startadvies en praktische startbegeleiding.
              </p>
              <p className="rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm leading-relaxed text-mmTextMuted mb-5">
                Dit is geen volledig 1-op-1 coachingstraject, maar een begeleid
                starttraject waarmee je gericht en veilig kunt beginnen.
              </p>
              <ul className="grid gap-2 mb-6 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {startTrajectoryCardItems.map((item) => (
                  <li
                    key={item}
                    className="flex gap-2 text-sm text-mmTextMuted leading-relaxed"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-mmAccent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact#contact-form" className="mm-btnPrimary mt-auto">
                Vraag een intake aan
              </Link>
            </article>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
                Afbakening
              </p>
              <h2 className="mm-h2 mb-6">Wat dit traject wel en niet is</h2>
              <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
                <p>
                  Dit starttraject helpt je om de eerste 12 weken gericht op te
                  bouwen en geeft je duidelijkheid over je huidige niveau,
                  trainingsstructuur en belangrijkste aandachtspunten.
                </p>
                <p>
                  Het is niet bedoeld als garantie dat je klaar bent voor een
                  opleiding of selectie. Voor zware militaire richtingen is
                  langere termijn opbouw nodig. Dit traject geeft je een sterke
                  start en een beter beeld van wat daarvoor nodig is.
                </p>
              </div>
              <div className="mt-7 rounded-2xl border border-white/10 bg-mmSurface px-5 py-5">
                <h3 className="font-display uppercase tracking-widest text-mmText text-lg md:text-xl mb-3">
                  Past dit traject bij jou?
                </h3>
                <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
                  Dit traject past vooral als je serieus wilt starten met een
                  gestructureerde voorbereiding richting Cluster 6, maar nog
                  niet verwacht dat je na 12 weken volledig klaar bent voor een
                  opleiding of selectie. Je hoeft niet al op niveau te zijn om
                  te beginnen. Je moet wel bereid zijn om rustig, consequent en
                  eerlijk te bouwen.
                </p>
                <p className="mt-4 text-mmTextMuted text-sm md:text-base leading-relaxed">
                  Heb je acute blessures, medische klachten of een selectie
                  binnen enkele weken, dan is persoonlijk overleg verstandiger
                  voordat je start.
                </p>
              </div>
            </div>

            <div className="mm-card p-6 md:p-8">
              <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
                Guide
              </p>
              <h3 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl mb-4">
                Cluster 6 Guide voor Vrouwen
              </h3>
              <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
                De bijbehorende guide gaat specifiek in op punten die in de
                voorbereiding van vrouwen vaak extra aandacht vragen:
                bovenlichaamkracht, pull-ups, push-ups, bepakking, voldoende
                eten om te herstellen en sterker te worden, trainen rond je
                cyclus, herstel en het opbouwen van belastbaarheid zonder
                steeds over je grens te gaan.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section id="routes" className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl mb-10">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Routes
            </p>
            <h2 className="mm-h2 mb-5">Relevante routes binnen Mission Movement</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Gebruik deze pagina's om de bredere voorbereiding, eenheden en je
              eigen startniveau beter te plaatsen.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {internalLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="mm-card group block p-5 transition-all duration-300 hover:-translate-y-[2px] hover:border-mmAccent/40"
              >
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-2">
                  {item.label}
                </h3>
                <p className="text-sm leading-relaxed text-mmTextMuted">
                  {item.text}
                </p>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Context
            </p>
            <h2 className="mm-h2 mb-6">Waarom dit nu relevant is</h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Misschien ben je via termen als Athena Force, Force Athena,
                vrouwen richting KCT, NLMARSOF vrouwen of Cluster 6 vrouwen op
                deze pagina terechtgekomen. De kern blijft hetzelfde: je zoekt
                een serieuze manier om fysiek op te bouwen richting een zware
                militaire richting.
              </p>
              <p>
                Mission Movement is geen onderdeel van Defensie en dit traject
                is geen officieel Athena Force traject. Dit aanbod is bedoeld
                voor vrouwen die zelfstandig of begeleid willen bouwen aan de
                fysieke basis, belastbaarheid en structuur die nodig zijn om
                richting Cluster 6 en zware militaire opleidingen te groeien.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-10 md:py-12 bg-mmPage border-b border-mmBorder">
        <Container>
          <div className="mm-card max-w-4xl p-6 md:p-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="max-w-2xl">
                <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-3">
                  Meer context lezen?
                </p>
                <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
                  Lees ook de blog over Athena Force, Cluster 6 en vrouwen
                  richting Special Forces. Gebruik voor actuele keuringseisen
                  de officiële bronnen op deze pagina en de informatie bij je
                  eigen vacature en uitnodiging.
                </p>
              </div>
              <Link
                to="/blogs/athena-force-cluster-6-vrouwen-special-forces-voorbereiding"
                className="mm-btn inline-flex justify-center shrink-0"
              >
                Lees de blog
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl mb-10">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Veel gezocht
            </p>
            <h2 className="mm-h2 mb-5">Veel gezocht, duidelijk uitgelegd</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Korte antwoorden op vragen die vaak opkomen bij vrouwen die
              zoeken naar Athena Force, Force Athena, vrouwen KCT, vrouw
              commando worden, NLMARSOF vrouwen, special forces vrouwen,
              Cluster 6 vrouwen of Defensie Cluster 6 vrouwen.
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            {faqItems.map((item) => (
              <article key={item.question} className="mm-card p-6">
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.question}
                </h3>
                <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
                  {item.answer}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20">
        <Container>
          <div className="mm-card max-w-4xl mx-auto p-7 md:p-10 text-center">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-3">
              Eerste stap
            </p>
            <h2 className="mm-h2 mb-4">
              Wil je weten of deze route logisch is voor jou?
            </h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-7">
              Stuur je doel, startniveau en eventuele klachten of twijfels mee.
              Dan kijken we welke opbouw het meest passend is.
            </p>
            <Link to="/contact#contact-form" className="mm-btnPrimary">
              Vraag informatie aan
            </Link>
          </div>
        </Container>
      </section>
    </main>
  );
};

export default Cluster6VoorbereidingVrouwen;
